import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Skeleton,
  Menu as MuiMenu,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useNavigate } from "react-router-dom";
import {
  archiveCampaign,
  duplicateCampaign,
  ICampaign,
  listCampaigns,
} from "../../../Api/campaignsApi";
import { useNotifier } from "../../../Core/Notifier";
import { useConfirm } from "../../../Components/Confirm";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import { surfaceSx } from "../../Course/shared/moduleSx";

type Tab = "all" | "draft" | "scheduled" | "sending" | "sent" | "failed" | "archived";

const STATUS_STYLES: Record<string, { bg: string; fg: string }> = {
  draft:          { bg: "#F1F5F9", fg: "#475569" },
  scheduled:      { bg: "#DBEAFE", fg: "#1D4ED8" },
  queued:         { bg: "#FEF3C7", fg: "#92400E" },
  sending:        { bg: "#DBEAFE", fg: "#1D4ED8" },
  sent:           { bg: "#DCFCE7", fg: "#15803D" },
  partially_sent: { bg: "#FEF3C7", fg: "#92400E" },
  failed:         { bg: "#FEE2E2", fg: "#B91C1C" },
  cancelled:      { bg: "#F1F5F9", fg: "#475569" },
  archived:       { bg: "#F1F5F9", fg: "#64748B" },
};

const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40, borderRadius: "10px", bgcolor: "#FFFFFF",
    "& fieldset": { borderColor: B13.line },
    "&:hover fieldset": { borderColor: tint12(B13.primary) },
    "&.Mui-focused fieldset": { borderColor: B13.primary, borderWidth: "1px" },
  },
  "& .MuiOutlinedInput-input": { fontSize: 13.5, color: B13.ink },
} as const;

const CampaignsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const confirm = useConfirm();

  const [rows, setRows]           = useState<ICampaign[]>([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState<Tab>("all");
  const [search, setSearch]       = useState("");
  const [channel, setChannel]     = useState<string>("");
  const [rowMenu, setRowMenu]     = useState<{ anchor: HTMLElement; row: ICampaign } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCampaigns({
        status: tab === "all" ? undefined : tab,
        channel: channel || undefined,
        per_page: 100,
      });
      setRows(res.data);
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to load campaigns.");
    } finally {
      setLoading(false);
    }
  }, [tab, channel, showErrorMessage]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (! search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) => r.name.toLowerCase().includes(q));
  }, [rows, search]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length, draft: 0, scheduled: 0, sent: 0, failed: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] ?? 0) + 1; });
    return c;
  }, [rows]);

  const handleDuplicate = async (row: ICampaign) => {
    setRowMenu(null);
    try {
      const clone = await duplicateCampaign(row.id);
      showMessage(`Duplicated "${clone.name}".`);
      navigate(`/communication/newsletters/${clone.id}/edit`);
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to duplicate campaign.");
    }
  };

  const handleArchive = async (row: ICampaign) => {
    setRowMenu(null);
    await confirm({
      title: `Archive "${row.name}"?`,
      message: "Archived campaigns are hidden from the default list but their history is preserved.",
      confirmText: "Archive",
      variant: "danger",
      onConfirm: async () => {
        try {
          await archiveCampaign(row.id);
          showMessage("Archived.");
          load();
        } catch (e: any) {
          showErrorMessage(e?.message || "Failed to archive campaign.");
          throw e;
        }
      },
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#F7F9FC", minHeight: "100%" }}>
      {/* ── HEADER ─────────────────────────────────────────────── */}
      <Box sx={{ ...surfaceSx, p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}
               alignItems={{ xs: "flex-start", md: "center" }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2.5, display: "flex",
            alignItems: "center", justifyContent: "center",
            bgcolor: tint12(B13.primary), color: B13.primary, flexShrink: 0,
            "& svg": { fontSize: 24 },
          }}><CampaignOutlinedIcon /></Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...B13TypeScale.h2, letterSpacing: "-0.01em", lineHeight: 1.15 }}>
              Newsletters & Campaigns
            </Typography>
            <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2, mt: 0.25, maxWidth: 720 }}>
              Create and manage Email and SMS campaigns for your Step Up community.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/communication/newsletters/create")}
            sx={{
              textTransform: "none", bgcolor: B13.primary,
              "&:hover": { bgcolor: B13.primary },
              borderRadius: "10px", height: 40, px: 2, fontWeight: 600,
            }}
          >Create Campaign</Button>
        </Stack>
      </Box>

      {/* ── SUMMARY CARDS ──────────────────────────────────────── */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(5, 1fr)" },
        gap: 1.5, mb: 2,
      }}>
        {[
          { label: "Total",     value: counts.all,       tone: B13.primary },
          { label: "Draft",     value: counts.draft ?? 0, tone: "#64748b" },
          { label: "Scheduled", value: counts.scheduled ?? 0, tone: "#1D4ED8" },
          { label: "Sent",      value: counts.sent ?? 0,      tone: "#15803D" },
          { label: "Failed",    value: counts.failed ?? 0,    tone: "#B91C1C" },
        ].map((c) => (
          <Box key={c.label} sx={{ ...surfaceSx, p: 2, borderRadius: 3 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.6 }}>
              {c.label}
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: c.tone, mt: 0.5, letterSpacing: "-0.02em" }}>
              {c.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── TABS + FILTERS ─────────────────────────────────────── */}
      <Box sx={{ ...surfaceSx, p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}
               alignItems={{ xs: "stretch", md: "center" }}>
          <Stack direction="row" spacing={0.5} sx={{ bgcolor: "#F1F5F9", borderRadius: "10px", p: 0.5, flexWrap: "wrap" }}>
            {(["all","draft","scheduled","sending","sent","failed","archived"] as Tab[]).map((t) => {
              const active = t === tab;
              return (
                <Box key={t} component="button" type="button" onClick={() => setTab(t)}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: 0.75,
                    px: 1.25, height: 30, borderRadius: "8px", border: "none",
                    cursor: "pointer", font: "inherit", fontWeight: 600, fontSize: 12.5,
                    bgcolor: active ? "#FFFFFF" : "transparent",
                    color:   active ? B13.primary : B13Ink.ink2,
                    boxShadow: active ? "0 1px 2px rgba(15,23,42,0.08)" : "none",
                    textTransform: "capitalize",
                  }}
                >
                  {t.replace("_", " ")}
                </Box>
              );
            })}
          </Stack>
          <TextField
            size="small" placeholder="Search campaign name…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ ...filterFieldSx, flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: B13Ink.ink3 }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select size="small" label="Channel" value={channel}
            onChange={(e) => setChannel(e.target.value)}
            sx={{ ...filterFieldSx, minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="sms">SMS</MenuItem>
          </TextField>
        </Stack>
      </Box>

      {/* ── LIST ───────────────────────────────────────────────── */}
      <Box sx={{ ...surfaceSx, borderRadius: 3, overflow: "hidden" }}>
        {loading ? (
          <Stack spacing={1.25} sx={{ p: 2 }}>
            {[0,1,2,3].map((i) =>
              <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: "12px" }} />
            )}
          </Stack>
        ) : filtered.length === 0 ? (
          <Box sx={{ p: { xs: 4, md: 6 }, textAlign: "center" }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: 3, mx: "auto", mb: 1.5,
              bgcolor: tint12(B13.primary), color: B13.primary,
              display: "flex", alignItems: "center", justifyContent: "center",
              "& svg": { fontSize: 28 },
            }}><CampaignOutlinedIcon /></Box>
            <Typography sx={{ ...B13TypeScale.h3, mb: 0.5 }}>No campaigns yet</Typography>
            <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2, mb: 2 }}>
              Create a newsletter, announcement or SMS campaign to reach your community.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => navigate("/communication/newsletters/create")}
              sx={{ textTransform: "none", bgcolor: B13.primary,
                    "&:hover": { bgcolor: B13.primary }, borderRadius: "10px" }}
            >Create Campaign</Button>
          </Box>
        ) : (
          <Stack divider={<Box sx={{ borderBottom: `1px solid ${B13.line}` }} />}>
            {filtered.map((row) => {
              const ChannelIcon = row.channel === "email" ? EmailOutlinedIcon : SmsOutlinedIcon;
              const chStyle = row.channel === "email" ? "#1F6FEB" : "#22c55e";
              const statusStyle = STATUS_STYLES[row.status] ?? STATUS_STYLES.draft;
              return (
                <Box key={row.id} sx={{
                  px: { xs: 1.5, md: 2 }, py: 1.5,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr auto", md: "1fr 120px 140px 120px 100px auto" },
                  gap: { xs: 1, md: 2 }, alignItems: "center",
                  transition: "background-color 120ms ease",
                  "&:hover": { bgcolor: "#F8FAFC" },
                }}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 2, flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      bgcolor: `${chStyle}1A`, color: chStyle,
                    }}><ChannelIcon fontSize="small" /></Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 14, fontWeight: 700, color: B13.ink,
                          cursor: "pointer",
                          "&:hover": { color: B13.primary },
                        }}
                        onClick={() => navigate(`/communication/newsletters/${row.id}`)}
                      >{row.name}</Typography>
                      <Typography sx={{ fontSize: 12, color: B13Ink.ink2, mt: 0.25 }} noWrap>
                        {row.classification === "marketing" ? "Marketing" : "Transactional"} · {row.purpose}
                      </Typography>
                    </Box>
                  </Stack>
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <Chip size="small" label={row.channel.toUpperCase()}
                      sx={{ height: 22, fontSize: 11, fontWeight: 700,
                            bgcolor: `${chStyle}14`, color: chStyle }} />
                  </Box>
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <Typography sx={{ fontSize: 12.5, color: B13Ink.ink2 }}>
                      Recipients: <b>{row.total_recipients}</b>
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: B13Ink.ink2, mt: 0.25 }}>
                      Sent {row.sent_count} · Skipped {row.skipped_count} · Failed {row.failed_count}
                    </Typography>
                  </Box>
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <Chip size="small"
                      label={row.status.replace("_", " ")}
                      sx={{ height: 22, fontSize: 11, fontWeight: 700,
                            bgcolor: statusStyle.bg, color: statusStyle.fg,
                            textTransform: "capitalize" }} />
                  </Box>
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <Typography sx={{ fontSize: 11.5, color: B13Ink.ink2 }}>
                      {row.scheduled_at
                        ? new Date(row.scheduled_at).toLocaleDateString()
                        : row.completed_at
                          ? new Date(row.completed_at).toLocaleDateString()
                          : new Date(row.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => navigate(`/communication/newsletters/${row.id}`)}>
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {(row.status === "draft" || row.status === "scheduled") && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => navigate(`/communication/newsletters/${row.id}/edit`)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="More">
                      <IconButton size="small" onClick={(e) => setRowMenu({ anchor: e.currentTarget, row })}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <MuiMenu anchorEl={rowMenu?.anchor ?? null} open={Boolean(rowMenu)} onClose={() => setRowMenu(null)}>
        <MenuItem onClick={() => rowMenu && handleDuplicate(rowMenu.row)}>
          <ContentCopyOutlinedIcon fontSize="small" sx={{ mr: 1 }} />Duplicate
        </MenuItem>
        <MenuItem onClick={() => rowMenu && handleArchive(rowMenu.row)}
                  disabled={rowMenu?.row.status === "archived"}>
          <Inventory2OutlinedIcon fontSize="small" sx={{ mr: 1 }} />Archive
        </MenuItem>
      </MuiMenu>
    </Box>
  );
};

export default CampaignsListPage;
